import React, { useLayoutEffect, useRef, useState } from 'react'
import { Image, Menu } from 'semantic-ui-react'
import styled from 'styled-components'
import { List, Info } from 'phosphor-react'
import { FlexRow } from 'components'
import { Images } from 'assets'
import { AboutModal } from '../components/Modal'
import { ProfileDropdown } from '../components/ProfileDropdown'
import { AppDownloadButtons } from '../components/AppDownloadButtons'
import { PlanUsageChip } from '../components/PlanUsageChip'

/** Two full store badges + gap (~140px each + padding/gap). */
const FULL_DOWNLOAD_WIDTH = 300
const EXPAND_BUFFER = 48
const GAP = 10
/** Logo / left cluster in the fixed header. */
const LEFT_RESERVE = 200
const RIGHT_PADDING = 28

const MenuContainer = styled(Menu)`
    position: fixed;
    width: 100%;
    z-index: 99;
    border: 0 !important;
    border-bottom: solid 1px #e8e9e9 !important;
    border-radius: 0px !important;
    box-shadow: none !important;
    top: 0;
    min-height: 55px !important;
    display: flex !important;
    align-items: center !important;
    .logo {
        background: ${({ theme }) => theme.colors.primary};
    }
    .logout {
        line-height: 0.6rem;
    }
    .ui.menu .item {
        align-self: center;
    }
    & > .right.menu {
        display: flex !important;
        align-items: center !important;
        float: none !important;
        margin-left: auto !important;
        margin-right: 0 !important;
        /* Force remaining header width (Semantic UI otherwise shrink-wraps) */
        flex: 1 1 0% !important;
        width: 0 !important;
        min-width: 0 !important;
        max-width: none !important;
        justify-content: flex-end !important;
    }
`

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: nowrap;
    gap: ${GAP}px;
    margin-right: 12px;
    min-width: 0;
    width: 100%;
`

const EndCluster = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
`

const DownloadSlot = styled.div`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    & > div {
        margin: 0 !important;
    }
`

const ChipSlot = styled.div`
    display: flex;
    align-items: center;
    flex: 0 0 auto;
    max-width: 340px;
`

const IconButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    line-height: 0;
`

export function HeaderBar({ setVisible, visible }) {
    const [aboutOpen, setAboutOpen] = useState(false)
    // Default to full on desktop-sized viewports
    const [compactDownloads, setCompactDownloads] = useState(
        () =>
            typeof window !== 'undefined' ? window.innerWidth < 900 : false,
    )
    const actionsRef = useRef(null)
    const chipRef = useRef(null)
    const endRef = useRef(null)

    useLayoutEffect(() => {
        const update = () => {
            const chipWidth = chipRef.current?.offsetWidth || 0
            const endWidth = endRef.current?.offsetWidth || 0
            // Use viewport width — reliable even if Semantic menu shrink-wraps
            const viewport = window.innerWidth
            const available =
                viewport -
                LEFT_RESERVE -
                chipWidth -
                endWidth -
                GAP * 2 -
                RIGHT_PADDING

            const actions = actionsRef.current
            // Secondary check: if full badges are showing and row overflows, compact
            const overflowing =
                actions &&
                !compactDownloads &&
                actions.scrollWidth > actions.clientWidth + 2

            setCompactDownloads(prev => {
                if (overflowing) return true
                if (prev) {
                    return available < FULL_DOWNLOAD_WIDTH + EXPAND_BUFFER
                }
                return available < FULL_DOWNLOAD_WIDTH
            })
        }

        update()
        window.addEventListener('resize', update)

        const ro =
            typeof ResizeObserver !== 'undefined'
                ? new ResizeObserver(update)
                : null
        if (ro) {
            if (actionsRef.current) ro.observe(actionsRef.current)
            if (chipRef.current) ro.observe(chipRef.current)
            if (endRef.current) ro.observe(endRef.current)
        }

        return () => {
            window.removeEventListener('resize', update)
            ro?.disconnect()
        }
        // Re-bind when compact mode flips so overflow check sees the new layout
    }, [compactDownloads])

    return (
        <MenuContainer className="mt-0 menu-header">
            {!visible && (
                <div
                    className="d-flex align-items-center ms-3"
                    onClick={() => setVisible(true)}
                >
                    {false && <List size={24} />}
                    <FlexRow className="align-items-center">
                        <Image className="logo" src={Images.Logo} size="small" />
                        <span className="ms-2">EAuditor</span>
                    </FlexRow>
                </div>
            )}
            <Menu.Menu position="right">
                <HeaderActions ref={actionsRef}>
                    <DownloadSlot>
                        <AppDownloadButtons
                            variant={compactDownloads ? 'compact' : 'full'}
                        />
                    </DownloadSlot>
                    <ChipSlot ref={chipRef}>
                        <PlanUsageChip />
                    </ChipSlot>
                    <EndCluster ref={endRef}>
                        <IconButton
                            type="button"
                            onClick={() => setAboutOpen(true)}
                            title="About"
                        >
                            <Info size={24} />
                        </IconButton>
                        <ProfileDropdown />
                    </EndCluster>
                    <AboutModal
                        open={aboutOpen}
                        onClose={() => setAboutOpen(false)}
                    />
                </HeaderActions>
            </Menu.Menu>
        </MenuContainer>
    )
}
